import React, { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const EMPTY_STATE = {
  active_provider: "gemini",
  providers: [
    { provider: "gemini", label: "Google Gemini", runtime_supported: true, models: ["gemini-1.5-flash", "gemini-1.5-pro", "gemini-2.0-flash-exp"], selected_model: "gemini-1.5-flash", api_key: "", has_key: false, updated_at: null },
    { provider: "openai", label: "OpenAI", runtime_supported: true, models: ["gpt-4.1", "gpt-4.1-mini", "gpt-5"], selected_model: "gpt-4.1", api_key: "", has_key: false, updated_at: null },
    { provider: "anthropic", label: "Anthropic Claude", runtime_supported: true, models: ["claude-3-7-sonnet", "claude-3-5-sonnet", "claude-3-5-haiku"], selected_model: "claude-3-7-sonnet", api_key: "", has_key: false, updated_at: null }
  ]
};

export default function SettingsPage() {
  const [settings, setSettings] = useState(EMPTY_STATE);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testingProvider, setTestingProvider] = useState("");
  const [providerStatus, setProviderStatus] = useState({});

  const runtimeProvider = useMemo(
    () => settings.providers.find((provider) => provider.provider === settings.active_provider) || settings.providers[0],
    [settings]
  );

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/ai/settings");
      const providers = EMPTY_STATE.providers.map((fallback) => {
        const remote = (data.providers || []).find((item) => item.provider === fallback.provider) || {};
        return {
          ...fallback,
          ...remote,
          api_key: "",
        };
      });
      setSettings({
        active_provider: data.preferences?.active_provider || "gemini",
        providers,
      });
      setProviderStatus(data.preferences?.provider_tests || {});
    } catch {
      setStatus("Unable to load AI settings right now. You can still enter new provider values and save them.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const updateProvider = (providerKey, patch) => {
    setSettings((prev) => ({
      ...prev,
      providers: prev.providers.map((provider) => (
        provider.provider === providerKey ? { ...provider, ...patch } : provider
      ))
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus("");
    try {
      await client.post("/ai/settings", {
        active_provider: settings.active_provider,
        models: settings.providers.reduce((acc, provider) => {
          acc[provider.provider] = provider.selected_model;
          return acc;
        }, {}),
        providers: settings.providers
          .filter((provider) => provider.api_key.trim())
          .map((provider) => ({
            provider: provider.provider,
            api_key: provider.api_key.trim()
          }))
      });
      setStatus("AI provider settings saved successfully.");
      await loadSettings();
    } catch {
      setStatus("Unable to save AI settings. Please verify API connectivity and try again.");
    } finally {
      setSaving(false);
    }
  };

  const testProviderConnection = async (provider) => {
    setTestingProvider(provider.provider);
    setProviderStatus((prev) => ({ ...prev, [provider.provider]: "" }));
    try {
      const { data } = await client.post("/ai/settings/test", {
        provider: provider.provider,
        model: provider.selected_model,
        api_key: provider.api_key.trim() || undefined
      });
      setProviderStatus((prev) => ({
        ...prev,
        [provider.provider]: {
          ok: Boolean(data.ok),
          message: data.message || "Connection verified.",
          tested_at: new Date().toISOString(),
          model: provider.selected_model
        }
      }));
    } catch (error) {
      setProviderStatus((prev) => ({
        ...prev,
        [provider.provider]: {
          ok: false,
          message: error?.response?.data?.message || "Connection test failed.",
          tested_at: new Date().toISOString(),
          model: provider.selected_model
        }
      }));
    } finally {
      setTestingProvider("");
      await loadSettings();
    }
  };

  return (
    <main className="page-grid">
      <section className="card-modern settings-card settings-card-wide">
        <div className="section-head">
          <div>
            <h3>AI Control Center</h3>
            <p className="muted">Manage providers, choose preferred models, and decide which provider should be active for report drafting and field assistance.</p>
          </div>
          <span className={`status-chip ${runtimeProvider?.runtime_supported ? "status-active" : "status-monitoring"}`}>
            {runtimeProvider?.label || "Provider"} {runtimeProvider?.runtime_supported ? "Available Now" : "Configured For Later"}
          </span>
        </div>

        <form onSubmit={save} className="settings-form-grid">
          <section className="settings-active-provider">
            <label>
              <span>Active Provider</span>
              <select value={settings.active_provider} onChange={(event) => setSettings((prev) => ({ ...prev, active_provider: event.target.value }))}>
                {settings.providers.map((provider) => (
                  <option key={provider.provider} value={provider.provider}>{provider.label}</option>
                ))}
              </select>
            </label>
            <p className="muted">The active provider selected here is now used by field assistance and AI-seeded report drafting, as long as that provider has a valid key on file.</p>
          </section>

          <section className="settings-provider-grid">
            {settings.providers.map((provider) => (
              <article key={provider.provider} className={`settings-provider-card ${settings.active_provider === provider.provider ? "active" : ""}`}>
                <div className="settings-provider-head">
                  <div>
                    <h4>{provider.label}</h4>
                    <p className="muted">
                      {provider.has_key ? `Key on file${provider.updated_at ? ` • updated ${provider.updated_at}` : ""}` : "No key saved yet"}
                    </p>
                  </div>
                  <span className={`outline-status-chip outline-status-${provider.runtime_supported ? "low" : "medium"}`}>
                    {provider.runtime_supported ? "Runtime Ready" : "Pre-configure"}
                  </span>
                </div>

                <label>
                  <span>Preferred Model</span>
                  <select value={provider.selected_model} onChange={(event) => updateProvider(provider.provider, { selected_model: event.target.value })}>
                    {provider.models.map((model) => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>{provider.has_key ? "Replace API Key" : "API Key"}</span>
                  <input
                    type="password"
                    placeholder={provider.has_key ? "Leave blank to keep the saved key" : `${provider.label} API key`}
                    value={provider.api_key}
                    onChange={(event) => updateProvider(provider.provider, { api_key: event.target.value })}
                  />
                </label>

                <div className="settings-provider-actions">
                  <button
                    type="button"
                    className="button-link ghost compact-action-btn"
                    onClick={() => testProviderConnection(provider)}
                    disabled={testingProvider !== "" && testingProvider !== provider.provider}
                  >
                    {testingProvider === provider.provider ? "Testing..." : "Test Connection"}
                  </button>
                  {providerStatus[provider.provider]?.message ? (
                    <div className={`settings-provider-badge settings-provider-badge-${providerStatus[provider.provider]?.ok ? "ok" : "fail"}`}>
                      <strong>{providerStatus[provider.provider]?.ok ? "Verified" : "Needs Attention"}</strong>
                      <span>{providerStatus[provider.provider]?.message}</span>
                      {providerStatus[provider.provider]?.tested_at ? (
                        <small>Tested {String(providerStatus[provider.provider].tested_at).replace("T", " ").slice(0, 16)}</small>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </article>
            ))}
          </section>

          <div className="settings-submit-row">
            <button type="submit" disabled={saving || loading}>{saving ? "Saving..." : "Save AI Settings"}</button>
          </div>
        </form>

        {status ? <p className="status-text">{status}</p> : null}
      </section>
    </main>
  );
}
